/**
 * <AdminCategoriesTab />
 *
 * Two-level category management for admins.  Backed by:
 *
 *   GET    /api/admin/categories            -- full nested tree (incl. inactive)
 *   POST   /api/admin/categories            -- create top-level / child
 *   PATCH  /api/admin/categories/{id}       -- rename, reparent, archive, reorder
 *   DELETE /api/admin/categories/{id}       -- hard / soft / blocked-by-children
 *
 * Tree is rendered flat with one level of left-indent on children.  Parent
 * rows expose "Add child"; child rows do not.  Edit/Archive/Delete + Move
 * up/Move down on every row.  Optimistic updates: we mutate the local tree
 * first, then roll back on API failure.
 */
import * as React from "react";
import {
  AlertCircle,
  Archive,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  FolderPlus,
  Layers,
  Pencil,
  Plus,
  RefreshCcw,
  Trash2,
  Undo2,
} from "lucide-react";
import { api, extractApiError } from "../../../lib/api";
import { Badge } from "../../../components/ui/Badge";
import { Button } from "../../../components/ui/Button";
import { Card, CardBody, CardHeader } from "../../../components/ui/Card";
import { Input } from "../../../components/ui/Input";
import { Modal } from "../../../components/ui/Modal";
import { cn } from "../../../lib/cn";

interface CategoryNode {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  parent_id: string | null;
  display_order: number;
  is_active: boolean;
  thread_count: number;
  product_count: number;
  created_at: string;
  updated_at: string;
  children: CategoryNode[];
}

// ---------------------------------------------------------------------------
// Tree helpers (immutable)
// ---------------------------------------------------------------------------
function replaceNode(
  tree: CategoryNode[],
  id: string,
  updater: (n: CategoryNode) => CategoryNode,
): CategoryNode[] {
  return tree.map((n) => {
    if (n.id === id) return updater(n);
    if (n.children.length) {
      return { ...n, children: replaceNode(n.children, id, updater) };
    }
    return n;
  });
}

function removeNode(tree: CategoryNode[], id: string): CategoryNode[] {
  return tree
    .filter((n) => n.id !== id)
    .map((n) =>
      n.children.length ? { ...n, children: removeNode(n.children, id) } : n,
    );
}

function findNode(tree: CategoryNode[], id: string): CategoryNode | null {
  for (const n of tree) {
    if (n.id === id) return n;
    const c = findNode(n.children, id);
    if (c) return c;
  }
  return null;
}

// Counts every node (top-level + children) as active or archived, and
// collects the archived ones as a flat list (with their parent's name for
// context, since they're pulled out of the tree's indentation).
function splitByActive(
  tree: CategoryNode[],
): { activeCount: number; archived: (CategoryNode & { parentName: string | null })[] } {
  let activeCount = 0;
  const archived: (CategoryNode & { parentName: string | null })[] = [];
  for (const root of tree) {
    if (root.is_active) activeCount++;
    else archived.push({ ...root, parentName: null });
    for (const child of root.children) {
      if (child.is_active) activeCount++;
      else archived.push({ ...child, parentName: root.name });
    }
  }
  return { activeCount, archived };
}

function moveInList(arr: CategoryNode[], id: string, delta: -1 | 1): CategoryNode[] {
  const i = arr.findIndex((x) => x.id === id);
  if (i < 0) return arr;
  const j = i + delta;
  if (j < 0 || j >= arr.length) return arr;
  const next = arr.slice();
  [next[i], next[j]] = [next[j], next[i]];
  // Renumber display_order so the visible order is stable on reload.
  return next.map((n, idx) => ({ ...n, display_order: idx }));
}

// Archived rows are pulled out of the visible list (see the "Archived"
// collapsible section below), so up/down must reorder relative to the
// other ACTIVE siblings only — swap with the nearest active neighbour by
// id rather than by raw array index, leaving archived siblings in place.
function moveActiveInList(arr: CategoryNode[], id: string, delta: -1 | 1): CategoryNode[] {
  const activeIds = arr.filter((n) => n.is_active).map((n) => n.id);
  const ai = activeIds.indexOf(id);
  if (ai < 0) return arr;
  const aj = ai + delta;
  if (aj < 0 || aj >= activeIds.length) return arr;
  const otherId = activeIds[aj];
  const i = arr.findIndex((n) => n.id === id);
  const j = arr.findIndex((n) => n.id === otherId);
  if (i < 0 || j < 0) return arr;
  const next = arr.slice();
  [next[i], next[j]] = [next[j], next[i]];
  return next.map((n, idx) => ({ ...n, display_order: idx }));
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export function AdminCategoriesTab() {
  const [tree, setTree] = React.useState<CategoryNode[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const [createOpen, setCreateOpen] = React.useState(false);
  const [createParentId, setCreateParentId] = React.useState<string | null>(null);
  const [editing, setEditing] = React.useState<CategoryNode | null>(null);
  const [deleting, setDeleting] = React.useState<CategoryNode | null>(null);
  const [archivedOpen, setArchivedOpen] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get<CategoryNode[]>("/admin/categories");
      setTree(r.data);
    } catch (err) {
      setError(extractApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  // -------------------------------------------------------------------
  // Optimistic toggle archive / unarchive
  // -------------------------------------------------------------------
  const toggleArchive = async (node: CategoryNode) => {
    const next = !node.is_active;
    const previous = tree;
    setTree((t) => replaceNode(t, node.id, (n) => ({ ...n, is_active: next })));
    try {
      await api.patch(`/admin/categories/${node.id}`, { is_active: next });
    } catch (err) {
      setError(extractApiError(err));
      setTree(previous);
    }
  };

  // -------------------------------------------------------------------
  // Optimistic reorder within siblings
  // -------------------------------------------------------------------
  const move = async (node: CategoryNode, delta: -1 | 1) => {
    const previous = tree;
    let updatedSiblings: CategoryNode[] = [];

    let next: CategoryNode[];
    if (node.parent_id == null) {
      next = moveActiveInList(tree, node.id, delta);
      updatedSiblings = next.filter((n) => n.parent_id == null);
    } else {
      next = tree.map((root) =>
        root.id === node.parent_id
          ? { ...root, children: moveActiveInList(root.children, node.id, delta) }
          : root,
      );
      const root = next.find((n) => n.id === node.parent_id);
      if (root) updatedSiblings = root.children;
    }
    setTree(next);

    // Persist new display_order values for the affected sibling row.
    try {
      // We only need to send the two rows that swapped; mid-list updates
      // are O(n) but n<=~100 so don't optimise prematurely.
      await Promise.all(
        updatedSiblings.map((sib) =>
          api.patch(`/admin/categories/${sib.id}`, {
            display_order: sib.display_order,
          }),
        ),
      );
    } catch (err) {
      setError(extractApiError(err));
      setTree(previous);
    }
  };

  // -------------------------------------------------------------------
  // Create
  // -------------------------------------------------------------------
  const onCreated = (node: CategoryNode) => {
    setCreateOpen(false);
    setCreateParentId(null);
    if (node.parent_id) {
      setTree((t) =>
        t.map((root) =>
          root.id === node.parent_id
            ? { ...root, children: [...root.children, node] }
            : root,
        ),
      );
    } else {
      setTree((t) => [...t, node]);
    }
  };

  // -------------------------------------------------------------------
  // Edit
  // -------------------------------------------------------------------
  const onEdited = (node: CategoryNode) => {
    setEditing(null);
    setTree((t) => replaceNode(t, node.id, (_n) => node));
  };

  // -------------------------------------------------------------------
  // Delete
  // -------------------------------------------------------------------
  const onDeleted = (id: string, archivedOnly: boolean) => {
    setDeleting(null);
    if (archivedOnly) {
      setTree((t) => replaceNode(t, id, (n) => ({ ...n, is_active: false })));
    } else {
      setTree((t) => removeNode(t, id));
    }
  };

  // -------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------
  const { activeCount, archived } = splitByActive(tree);
  const activeTree = tree
    .filter((root) => root.is_active)
    .map((root) => ({ ...root, children: root.children.filter((c) => c.is_active) }));

  return (
    <Card elevated data-testid="admin-categories-card">
      <CardHeader>
        <div>
          <h2 className="text-xl font-bold inline-flex items-center gap-2">
            <Layers className="h-5 w-5" aria-hidden /> Categories
          </h2>
          <p className="text-ink-muted text-sm mt-1">
            Two-level hierarchy. Parents at top level, children indented one
            level. Slug must be unique. Cannot create grandchildren.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            loading={loading}
            leadingIcon={<RefreshCcw className="h-4 w-4" />}
            data-testid="admin-categories-refresh"
          >
            Refresh
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              setCreateParentId(null);
              setCreateOpen(true);
            }}
            leadingIcon={<Plus className="h-4 w-4" />}
            data-testid="admin-categories-new-toplevel"
          >
            Add top-level
          </Button>
        </div>
      </CardHeader>
      <CardBody>
        {error && (
          <p
            data-testid="admin-categories-error"
            className="mb-3 inline-flex items-center gap-1.5 text-sm font-semibold text-brand"
          >
            <AlertCircle className="h-4 w-4" /> {error}
          </p>
        )}

        {tree.length > 0 && (
          <p
            data-testid="admin-categories-counter"
            className="mb-4 text-sm font-semibold text-ink-muted"
          >
            <span className="text-ink">{activeCount}</span> active categor
            {activeCount === 1 ? "y" : "ies"} ·{" "}
            <span className="text-brand">{archived.length}</span> archived (hidden from public)
          </p>
        )}

        {tree.length === 0 ? (
          <p
            data-testid="admin-categories-empty"
            className="text-ink-muted text-center py-8"
          >
            {loading ? "Loading…" : "No categories yet. Click \"Add top-level\" to create one."}
          </p>
        ) : (
          <ul className="flex flex-col divide-y divide-surface-border">
            {activeTree.map((root, idx) => (
              <Row
                key={root.id}
                node={root}
                depth={0}
                onEdit={() => setEditing(root)}
                onDelete={() => setDeleting(root)}
                onAddChild={() => {
                  setCreateParentId(root.id);
                  setCreateOpen(true);
                }}
                onArchiveToggle={() => toggleArchive(root)}
                onMoveUp={idx > 0 ? () => move(root, -1) : undefined}
                onMoveDown={
                  idx < activeTree.length - 1 ? () => move(root, +1) : undefined
                }
              >
                {root.children.map((child, ci) => (
                  <Row
                    key={child.id}
                    node={child}
                    depth={1}
                    onEdit={() => setEditing(child)}
                    onDelete={() => setDeleting(child)}
                    onArchiveToggle={() => toggleArchive(child)}
                    onMoveUp={ci > 0 ? () => move(child, -1) : undefined}
                    onMoveDown={
                      ci < root.children.length - 1
                        ? () => move(child, +1)
                        : undefined
                    }
                  />
                ))}
              </Row>
            ))}
          </ul>
        )}

        {archived.length > 0 && (
          <div className="mt-6 rounded-xl border-2 border-surface-border overflow-hidden">
            <button
              type="button"
              onClick={() => setArchivedOpen((o) => !o)}
              className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-surface-sunken hover:bg-surface-border/40 transition-colors"
              data-testid="admin-categories-archived-toggle"
              aria-expanded={archivedOpen}
            >
              <span className="inline-flex items-center gap-2 font-bold text-ink">
                <Archive className="h-4 w-4" aria-hidden />
                Archived ({archived.length})
              </span>
              {archivedOpen ? (
                <ChevronDown className="h-4 w-4 text-ink-muted" aria-hidden />
              ) : (
                <ChevronRight className="h-4 w-4 text-ink-muted" aria-hidden />
              )}
            </button>
            {archivedOpen && (
              <ul className="flex flex-col divide-y divide-surface-border">
                {archived.map((node) => (
                  <ArchivedRow
                    key={node.id}
                    node={node}
                    onEdit={() => setEditing(node)}
                    onDelete={() => setDeleting(node)}
                    onArchiveToggle={() => toggleArchive(node)}
                  />
                ))}
              </ul>
            )}
          </div>
        )}
      </CardBody>

      <CreateCategoryModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        parentId={createParentId}
        parentName={
          createParentId
            ? findNode(tree, createParentId)?.name ?? null
            : null
        }
        onCreated={onCreated}
      />
      <EditCategoryModal
        node={editing}
        onClose={() => setEditing(null)}
        onSaved={onEdited}
      />
      <DeleteCategoryModal
        node={deleting}
        onClose={() => setDeleting(null)}
        onDone={onDeleted}
      />
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------
function Row(props: {
  node: CategoryNode;
  depth: 0 | 1;
  children?: React.ReactNode;
  onEdit: () => void;
  onDelete: () => void;
  onAddChild?: () => void;
  onArchiveToggle: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
}) {
  const { node, depth } = props;
  return (
    <>
      <li
        className="py-3 flex items-center gap-3 flex-wrap"
        data-testid={`category-row-${node.id}`}
        style={{ paddingLeft: depth === 1 ? "2rem" : undefined }}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-ink truncate">{node.name}</span>
            {node.thread_count > 0 && (
              <Badge variant="muted" size="sm">
                {node.thread_count} thread{node.thread_count === 1 ? "" : "s"}
              </Badge>
            )}
            {node.product_count > 0 && (
              <Badge variant="muted" size="sm">
                {node.product_count} product{node.product_count === 1 ? "" : "s"}
              </Badge>
            )}
          </div>
          <div className="text-xs text-ink-muted font-mono mt-0.5 truncate">
            /{node.slug}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label="Move up"
            onClick={props.onMoveUp}
            disabled={!props.onMoveUp}
            className={cn(
              "inline-flex h-12 w-12 items-center justify-center rounded-lg border-2 border-surface-border text-ink-muted",
              props.onMoveUp
                ? "hover:border-ink hover:text-ink"
                : "opacity-30 cursor-not-allowed",
            )}
            data-testid={`category-up-${node.id}`}
          >
            <ChevronUp className="h-4 w-4" aria-hidden />
          </button>
          <button
            type="button"
            aria-label="Move down"
            onClick={props.onMoveDown}
            disabled={!props.onMoveDown}
            className={cn(
              "inline-flex h-12 w-12 items-center justify-center rounded-lg border-2 border-surface-border text-ink-muted",
              props.onMoveDown
                ? "hover:border-ink hover:text-ink"
                : "opacity-30 cursor-not-allowed",
            )}
            data-testid={`category-down-${node.id}`}
          >
            <ChevronDown className="h-4 w-4" aria-hidden />
          </button>
          <Button
            variant="outline"
            size="sm"
            onClick={props.onEdit}
            leadingIcon={<Pencil className="h-4 w-4" />}
            data-testid={`category-edit-${node.id}`}
          >
            Edit
          </Button>
          {depth === 0 && props.onAddChild && (
            <Button
              variant="outline"
              size="sm"
              onClick={props.onAddChild}
              leadingIcon={<FolderPlus className="h-4 w-4" />}
              data-testid={`category-add-child-${node.id}`}
            >
              Add child
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={props.onArchiveToggle}
            leadingIcon={<Undo2 className="h-4 w-4" />}
            data-testid={`category-archive-${node.id}`}
          >
            {node.is_active ? "Archive" : "Unarchive"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={props.onDelete}
            leadingIcon={<Trash2 className="h-4 w-4" />}
            data-testid={`category-delete-${node.id}`}
          >
            Delete
          </Button>
        </div>
      </li>
      {props.children}
    </>
  );
}

// ---------------------------------------------------------------------------
// Archived row -- gray background + explicit "hidden from public" callout,
// rendered inside the collapsible Archived section instead of the main
// list. No move up/down (ordering is meaningless once hidden); no "Add
// child" (parent context is shown as a text hint instead).
// ---------------------------------------------------------------------------
function ArchivedRow(props: {
  node: CategoryNode & { parentName: string | null };
  onEdit: () => void;
  onDelete: () => void;
  onArchiveToggle: () => void;
}) {
  const { node } = props;
  return (
    <li
      className="py-3 px-4 flex items-center gap-3 flex-wrap bg-surface-sunken"
      data-testid={`category-row-${node.id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-ink-muted truncate">{node.name}</span>
          <span className="text-[10px] font-black uppercase tracking-widest text-brand bg-brand/10 rounded px-1.5 py-0.5">
            Hidden from public
          </span>
          {node.thread_count > 0 && (
            <Badge variant="muted" size="sm">
              {node.thread_count} thread{node.thread_count === 1 ? "" : "s"}
            </Badge>
          )}
          {node.product_count > 0 && (
            <Badge variant="muted" size="sm">
              {node.product_count} product{node.product_count === 1 ? "" : "s"}
            </Badge>
          )}
        </div>
        <div className="text-xs text-ink-muted font-mono mt-0.5 truncate">
          /{node.slug}
          {node.parentName && <span className="ml-2 font-sans">under {node.parentName}</span>}
        </div>
      </div>
      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="sm"
          onClick={props.onEdit}
          leadingIcon={<Pencil className="h-4 w-4" />}
          data-testid={`category-edit-${node.id}`}
        >
          Edit
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={props.onArchiveToggle}
          leadingIcon={<Undo2 className="h-4 w-4" />}
          data-testid={`category-archive-${node.id}`}
        >
          Unarchive
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={props.onDelete}
          leadingIcon={<Trash2 className="h-4 w-4" />}
          data-testid={`category-delete-${node.id}`}
        >
          Delete
        </Button>
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Create modal
// ---------------------------------------------------------------------------
function CreateCategoryModal({
  open,
  onClose,
  parentId,
  parentName,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  parentId: string | null;
  parentName: string | null;
  onCreated: (node: CategoryNode) => void;
}) {
  const [name, setName] = React.useState("");
  const [slug, setSlug] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) {
      setName("");
      setSlug("");
      setDescription("");
      setErr(null);
    }
  }, [open]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const body: Record<string, unknown> = { name: name.trim() };
      if (slug.trim()) body.slug = slug.trim();
      if (description.trim()) body.description = description.trim();
      if (parentId) body.parent_id = parentId;
      const r = await api.post("/admin/categories", body);
      // Endpoint returns a Category without children/counts; pad locally.
      onCreated({
        ...(r.data as any),
        children: [],
        thread_count: 0,
        product_count: 0,
      });
    } catch (e2) {
      setErr(extractApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={parentId ? "Add child category" : "Add top-level category"}
      description={
        parentId
          ? `Will be created under "${parentName ?? "parent"}".`
          : "Top-level categories can have child categories underneath them."
      }
      testid="category-create-modal"
    >
      <form onSubmit={submit} className="flex flex-col gap-3">
        <Input
          label="Name"
          required
          autoFocus
          minLength={2}
          maxLength={120}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Bayliner"
          data-testid="category-create-name"
        />
        <Input
          label="Slug (optional — auto-generated)"
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          placeholder="bayliner"
          hint="Lowercase, hyphenated. Leave blank to derive from the name."
          data-testid="category-create-slug"
        />
        <Input
          label="Description (optional)"
          maxLength={500}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Short context for forum users."
          data-testid="category-create-desc"
        />
        {err && (
          <p
            className="text-sm font-semibold text-brand"
            data-testid="category-create-error"
          >
            {err}
          </p>
        )}
        <div className="flex items-center justify-end gap-2 pt-2">
          <Button type="button" variant="outline" size="md" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="md"
            loading={busy}
            data-testid="category-create-submit"
          >
            Create
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Edit modal
// ---------------------------------------------------------------------------
function EditCategoryModal({
  node,
  onClose,
  onSaved,
}: {
  node: CategoryNode | null;
  onClose: () => void;
  onSaved: (updated: CategoryNode) => void;
}) {
  const [name, setName] = React.useState("");
  const [slug, setSlug] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (node) {
      setName(node.name);
      setSlug(node.slug);
      setDescription(node.description ?? "");
      setErr(null);
    }
  }, [node]);

  if (!node) return null;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const body: Record<string, unknown> = {};
      if (name.trim() && name.trim() !== node.name) body.name = name.trim();
      if (slug.trim() && slug.trim() !== node.slug) body.slug = slug.trim();
      if ((description ?? "").trim() !== (node.description ?? ""))
        body.description = description.trim() || null;
      if (Object.keys(body).length === 0) {
        onClose();
        setBusy(false);
        return;
      }
      const r = await api.patch(`/admin/categories/${node.id}`, body);
      // Server returns Category without children/counts. Keep local values.
      onSaved({
        ...node,
        ...(r.data as any),
        children: node.children,
        thread_count: node.thread_count,
        product_count: node.product_count,
      });
    } catch (e2) {
      setErr(extractApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={!!node}
      onClose={onClose}
      title="Edit category"
      description="Rename, change the slug, or update the description."
      testid="category-edit-modal"
    >
      <form onSubmit={submit} className="flex flex-col gap-3">
        <Input
          label="Name"
          required
          minLength={2}
          maxLength={120}
          value={name}
          onChange={(e) => setName(e.target.value)}
          data-testid="category-edit-name"
        />
        <Input
          label="Slug"
          required
          minLength={2}
          maxLength={140}
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          data-testid="category-edit-slug"
        />
        <Input
          label="Description"
          maxLength={500}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          data-testid="category-edit-desc"
        />
        {err && (
          <p
            className="text-sm font-semibold text-brand"
            data-testid="category-edit-error"
          >
            {err}
          </p>
        )}
        <div className="flex items-center justify-end gap-2 pt-2">
          <Button type="button" variant="outline" size="md" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="md"
            loading={busy}
            data-testid="category-edit-submit"
          >
            Save
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Delete modal
// ---------------------------------------------------------------------------
function DeleteCategoryModal({
  node,
  onClose,
  onDone,
}: {
  node: CategoryNode | null;
  onClose: () => void;
  onDone: (id: string, archivedOnly: boolean) => void;
}) {
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!node) setErr(null);
  }, [node]);

  if (!node) return null;

  const hasAttachments = node.thread_count > 0 || node.product_count > 0;
  const hasChildren = node.children.length > 0;

  const confirm = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await api.delete(`/admin/categories/${node.id}`);
      const archived = !!(r.data as any)?.archived;
      onDone(node.id, archived);
    } catch (e) {
      setErr(extractApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={!!node}
      onClose={onClose}
      title={`Delete "${node.name}"?`}
      description={
        hasChildren
          ? "Reparent or delete the children first; this request will fail."
          : hasAttachments
            ? `This category has ${node.thread_count} thread(s) and ${node.product_count} product(s). It will be archived (hidden from the public forum and marketplace) but content stays intact.`
            : "Hard delete. No attached threads or products."
      }
      testid="category-delete-modal"
    >
      {err && (
        <p
          className="mb-2 text-sm font-semibold text-brand"
          data-testid="category-delete-error"
        >
          {err}
        </p>
      )}
      <div className="flex items-center justify-end gap-2">
        <Button type="button" variant="outline" size="md" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="button"
          variant="primary"
          size="md"
          loading={busy}
          onClick={confirm}
          data-testid="category-delete-confirm"
        >
          {hasAttachments && !hasChildren ? "Archive" : "Delete"}
        </Button>
      </div>
    </Modal>
  );
}
